import React, { useEffect, useState, useCallback } from "react";
import { fetchClients, patchClientLinkedin } from "../../../services/clientService";
import { ClientListItem } from "../../../types/fyo";
import PageHeader from "../../shared/PageHeader/PageHeader";
import { formatDateLong } from "../../../utils/formatters";
import "./ClientsPage.css";

const LIMIT = 50;

export default function ClientsPage() {
  const [rows,    setRows]    = useState<ClientListItem[]>([]);
  const [total,   setTotal]   = useState(0);
  const [offset,  setOffset]  = useState(0);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  const [filterName,        setFilterName]        = useState("");
  const [filterIndustry,    setFilterIndustry]    = useState("");
  const [filterOwner,       setFilterOwner]       = useState("");
  const [filterNoLinkedin,  setFilterNoLinkedin]  = useState(false);

  const [applied, setApplied] = useState({ name: "", industry: "", owner: "", noLinkedin: false });

  // Inline LinkedIn panel state
  const [activeClientId,  setActiveClientId]  = useState<number | null>(null);
  const [linkedinValue,   setLinkedinValue]   = useState("");
  const [saving,          setSaving]          = useState(false);
  const [actionError,     setActionError]     = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchClients({
      name:              applied.name     || undefined,
      industry_vertical: applied.industry || undefined,
      client_owner:      applied.owner    || undefined,
      no_linkedin:       applied.noLinkedin || undefined,
      limit:  LIMIT,
      offset,
    })
      .then((r: any) => { setRows(r.clients ?? r.items ?? []); setTotal(r.total ?? 0); })
      .catch((e: any) => setError(e.message))
      .finally(() => setLoading(false));
  }, [applied, offset]);

  useEffect(() => { load(); }, [load]);

  const handleApply = () => {
    setApplied({ name: filterName, industry: filterIndustry, owner: filterOwner, noLinkedin: filterNoLinkedin });
    setOffset(0);
  };

  const openLinkedinPanel = (clientId: number) => {
    setActiveClientId(clientId);
    setLinkedinValue("");
    setActionError(null);
  };

  const closeLinkedinPanel = () => {
    setActiveClientId(null);
    setActionError(null);
  };

  const handleLinkedinSave = async () => {
    if (!activeClientId || !linkedinValue.trim()) return;
    setSaving(true);
    setActionError(null);
    try {
      await patchClientLinkedin(activeClientId, linkedinValue.trim());
      closeLinkedinPanel();
      load();
    } catch (e: any) {
      setActionError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fyo-page">
      <PageHeader title="FYO Companies" subtitle="Client account directory" />

      <div className="fyo-filter-bar">
        <input
          className="fyo-input"
          placeholder="Search by name"
          value={filterName}
          onChange={(e) => setFilterName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleApply()}
          style={{ width: 200 }}
        />
        <input
          className="fyo-input"
          placeholder="Industry vertical"
          value={filterIndustry}
          onChange={(e) => setFilterIndustry(e.target.value)}
          style={{ width: 180 }}
        />
        <input
          className="fyo-input"
          placeholder="Client owner"
          value={filterOwner}
          onChange={(e) => setFilterOwner(e.target.value)}
          style={{ width: 180 }}
        />
        <label className="fyo-toggle">
          <input type="checkbox" checked={filterNoLinkedin} onChange={(e) => setFilterNoLinkedin(e.target.checked)} />
          Missing LinkedIn
        </label>
        <button className="fyo-btn fyo-btn--primary" onClick={handleApply}>Apply</button>
        <button className="fyo-btn fyo-btn--ghost" onClick={() => {
          setFilterName(""); setFilterIndustry(""); setFilterOwner(""); setFilterNoLinkedin(false);
          setApplied({ name: "", industry: "", owner: "", noLinkedin: false });
          setOffset(0);
        }}>Clear</button>
      </div>

      {error && <div className="fyo-error-banner">{error}</div>}

      <div className="fyo-card fyo-card--no-pad">
        {loading ? (
          <p className="state-text state-text--muted" style={{ padding: "var(--space-6)" }}>Loading...</p>
        ) : (
          <>
            <div className="fyo-table-scroll">
              <table className="fyo-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Town</th>
                  <th>LinkedIn</th>
                  <th>Client Owner</th>
                  <th>Industry Vertical</th>
                  <th>Salesforce ID</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={7} className="fyo-muted" style={{ textAlign: "center", padding: "var(--space-6)" }}>No clients found.</td></tr>
                ) : rows.map((r, i) => (
                  <React.Fragment key={r.rms_client_id}>
                    <tr className={i % 2 === 1 ? "fyo-table__row--odd" : ""}>
                      <td className="fyo-table__cell--primary">{r.name}</td>
                      <td>{r.town || "—"}</td>
                      <td>
                        {r.linkedin_company_url
                          ? <a href={r.linkedin_company_url} target="_blank" rel="noopener noreferrer" className="fyo-link-btn" style={{ fontSize: "var(--font-size-sm)" }}>LinkedIn</a>
                          : <span className="fyo-muted">—</span>}
                      </td>
                      <td>{r.client_owner      || "—"}</td>
                      <td>{r.industry_vertical || "—"}</td>
                      <td>{r.salesforce_id     || "—"}</td>
                      <td>
                        {!r.linkedin_company_url && (
                          <button
                            className="fyo-btn fyo-btn--warning fyo-btn--sm"
                            onClick={() => activeClientId === r.rms_client_id ? closeLinkedinPanel() : openLinkedinPanel(r.rms_client_id)}
                          >
                            Missing LinkedIn
                          </button>
                        )}
                      </td>
                    </tr>
                    {activeClientId === r.rms_client_id && (
                      <tr>
                        <td colSpan={7} style={{ padding: 0 }}>
                          <div className="fyo-inline-action">
                            <span className="fyo-inline-action__label">Add LinkedIn URL for <strong>{r.name}</strong></span>
                            <input
                              className="fyo-input"
                              style={{ flex: 1 }}
                              value={linkedinValue}
                              onChange={(e) => setLinkedinValue(e.target.value)}
                              placeholder="https://www.linkedin.com/company/..."
                              autoFocus
                            />
                            {actionError && <span className="text-danger" style={{ fontSize: "var(--font-size-sm)" }}>{actionError}</span>}
                            <button
                              className="fyo-btn fyo-btn--primary fyo-btn--sm"
                              disabled={saving || !linkedinValue.trim()}
                              onClick={handleLinkedinSave}
                            >
                              {saving ? "Saving..." : "Save"}
                            </button>
                            <button className="fyo-btn fyo-btn--ghost fyo-btn--sm" onClick={closeLinkedinPanel}>Cancel</button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
              </table>
            </div>

            <div className="fyo-pagination">
              <span className="fyo-pagination__info">
                {total === 0 ? "No results" : `${offset + 1}–${Math.min(offset + LIMIT, total)} of ${total}`}
              </span>
              <button className="fyo-btn fyo-btn--ghost fyo-btn--sm" disabled={offset === 0}
                onClick={() => setOffset((o) => Math.max(0, o - LIMIT))}>Prev</button>
              <span className="fyo-muted">Page {Math.floor(offset / LIMIT) + 1} of {Math.ceil(total / LIMIT) || 1}</span>
              <button className="fyo-btn fyo-btn--ghost fyo-btn--sm" disabled={offset + LIMIT >= total}
                onClick={() => setOffset((o) => o + LIMIT)}>Next</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
